// moms-harness:anchor v2
// JIRA: LOGISPRD-13309
import { FAILURE_ACTION, type FailureReason } from '../queries/useModifySkuAttributes'

/** 요약 대상 — 등록 응답 행과 수정 실패를 같은 모양으로 받는다. */
export interface FailureLike {
  barcode: string
  reason: FailureReason | null
  reasonMessage: string | null
  detail: string | null
}

/** 실패 토스트 상한 — 개별 바코드는 이만큼만 보여 준다. 검증 실패 경로와 같은 값이다. */
export const FAILURE_SAMPLE_CAP = 5

/**
 * 실패를 **사유별로 묶어** 토스트를 만든다.
 *
 * 행마다 하나씩 띄우면 전 행 실패 시 화면이 잠긴다 — 이 화면은 한 번에 1,000행을 올리고,
 * **거래처 없는 화주(prod 20개 중 11개)로 올리면 전 행이 같은 사유로 실패**한다.
 * 같은 문구 1,000개는 정보가 아니라 방해다.
 */
export const summarizeFailures = (
  failures: FailureLike[],
): string[] => {
  const byReason = new Map<string, typeof failures>()
  failures.forEach((f) => {
    // **`detail` 까지 키에 넣는다.** `detail` 은 「사유를 좁히는 값」이라 같은 사유 안에서도
    // 행마다 다르다 — `ALREADY_OWNED_BY_OTHER_SHIPPER` 의 선점 화주가 그렇다. 사유만으로 묶고
    // 첫 행의 `detail` 을 그룹 문구에 붙이면, 30건이 서로 다른 화주에 선점돼 있어도
    // `30건 실패 — … (MWP-60)` 으로 보인다. **후속 행동을 안내하려는 문구가 잘못된 후속 행동을
    // 안내한다** — 사용자는 30건 전부 MWP-60 소유로 읽고 그 화주에 문의한다
    const key = `${f.reason ?? f.reasonMessage ?? 'UNKNOWN'}\u0000${f.detail ?? ''}`
    // **`push` 로 쌓는다.** `[...(get() ?? []), f]` 는 매번 새 배열을 복사해 O(n²) 이다 —
    // 같은 사유로 1,000건이 실패하는 경로(거래처 없는 화주)가 이 화면의 실제 시나리오라
    // 최악의 경우 임시 할당이 50만 원소에 이른다
    const existing = byReason.get(key)
    if (existing) existing.push(f)
    else byReason.set(key, [f])
  })

  return [...byReason.entries()].map(([, group]) => {
    const head = group[0]
    // **조회 결과로 분기한다.** `reason` 은 서버 JSON 값이라 타입 유니온이 런타임을 보장하지
    // 않는다. BE 가 7번째 사유를 추가하면 `FAILURE_ACTION[...]` 이 undefined 가 되어 사용자에게
    // 「… undefined」가 그대로 보인다 — 후속 행동을 안내하는 자리라 문구가 깨지면 안 된다
    const known = head.reason ? FAILURE_ACTION[head.reason] : undefined
    const action = known ? ` ${known}` : ''
    const sample = group
      .slice(0, FAILURE_SAMPLE_CAP)
      .map((f) => f.barcode || '(바코드 없음)')
      .join(', ')
    const more = group.length > FAILURE_SAMPLE_CAP ? ` 외 ${group.length - FAILURE_SAMPLE_CAP}건` : ''
    const detail = head.detail ? ` (${head.detail})` : ''
    return `${group.length}건 실패 — ${head.reasonMessage ?? '처리에 실패했습니다.'}${detail}${action}\n대상: ${sample}${more}`
  })
}
